'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function StudentLoanCalculator() {
  const t = useTranslations("tools.studentLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('simple');
  const [showExtraPayments, setShowExtraPayments] = useState(false);

  // Simple Calculator State
  const [simpleInputs, setSimpleInputs] = useState({
    balance: 30000,
    term: 10,
    rate: 6.8
  });
  const [simpleResults, setSimpleResults] = useState<any>(null);

  // Repayment Calculator State
  const [repaymentInputs, setRepaymentInputs] = useState({
    balance: 30000,
    payment: 350,
    rate: 6.8,
    extraMonthly: 150,
    extraYearly: 0,
    extraOnetime: 0
  });
  const [repaymentResults, setRepaymentResults] = useState<any>(null);

  // Projection Calculator State
  const [projectionInputs, setProjectionInputs] = useState({
    yearsToGrad: 2,
    annualAmount: 10000,
    currentBalance: 20000,
    term: 10,
    graceMonths: 6,
    rate: 6.8,
    payInterest: false
  });
  const [projectionResults, setProjectionResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    success: '#10b981',
    warning: '#f59e0b',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.75rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '1000px' },
      { maxWidth: '1200px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabsContainer: getResponsiveStyle(
      { display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
      {},
      { gap: '1rem', marginBottom: '2rem' },
      {}
    ),
    tabButton: (isActive: boolean) => getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        border: isActive ? 'none' : `2px solid #e2e8f0`,
        background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        color: isActive ? 'white' : colors.textSecondary,
        transition: 'all 0.3s ease',
        fontSize: '0.95rem'
      },
      {},
      {},
      {}
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: getResponsiveStyle(
      { marginBottom: '1.25rem' },
      {},
      { marginBottom: '1.5rem' },
      {}
    ),
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      {}
    ),
    inputWrapper: {
      position: 'relative' as const
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 600
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontSize: '0.9rem'
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.875rem 1.25rem' },
      { padding: '0.875rem 1.25rem' }
    ),
    inputWithPrefix: {
      paddingLeft: '2.5rem'
    },
    inputWithSuffix: {
      paddingRight: '5rem'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem'
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer'
    },
    extraPayments: (show: boolean) => ({
      marginLeft: '1.5rem',
      marginTop: '0.75rem',
      display: show ? 'block' : 'none'
    }),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultItem: (highlight: boolean = false) => getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '0.75rem',
        background: highlight ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : '#f8fafc',
        borderRadius: '0.5rem',
        borderLeft: `4px solid ${highlight ? colors.success : '#e2e8f0'}`
      },
      {},
      {},
      {}
    ),
    resultLabel: getResponsiveStyle(
      { color: colors.textSecondary, fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      {}
    ),
    resultValue: (highlight: boolean = false) => getResponsiveStyle(
      { fontWeight: 700, color: highlight ? '#059669' : colors.textPrimary },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      {}
    ),
    chartContainer: {
      marginTop: '2rem',
      textAlign: 'center' as const
    },
    chartLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      marginTop: '1rem',
      flexWrap: 'wrap' as const
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    legendColor: (color: string) => ({
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      background: color
    }),
    comparisonTable: {
      marginTop: '2rem',
      overflowX: 'auto' as const
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    th: {
      padding: '1rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      fontWeight: 700,
      color: '#334155'
    },
    td: {
      padding: '1rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      color: colors.textSecondary
    },
    savingsHighlight: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '0.75rem',
        marginTop: '1.5rem',
        borderLeft: `4px solid ${colors.warning}`
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      {}
    ),
    savingsTitle: {
      color: '#92400e',
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
      fontWeight: 600
    },
    savingsText: {
      color: '#78350f',
      fontWeight: 600,
      fontSize: '1.25rem',
      margin: 0
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderLeft: `4px solid ${colors.primary}`,
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e2e8f0',
        marginBottom: '1rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.2s ease'
    }
  };

  const formatCurrency = (value: number): string => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTime = (months: number): string => {
    const years = Math.floor(months / 12);
    const remainingMonths = Math.round(months % 12);

    if (years === 0) {
      return remainingMonths + ' ' + (remainingMonths !== 1 ? t('months') : t('month'));
    } else if (remainingMonths === 0) {
      return years + ' ' + (years !== 1 ? t('years') : t('year'));
    } else {
      return years + ' ' + (years !== 1 ? t('years') : t('year')) + ' ' + t('and') + ' ' + remainingMonths + ' ' + (remainingMonths !== 1 ? t('months') : t('month'));
    }
  };

  const drawPieChart = (principal: number, interest: number): React.ReactNode => {
    const total = principal + interest;
    const principalPercent = (principal / total) * 100;
    const interestPercent = (interest / total) * 100;

    const cx = 100;
    const cy = 100;
    const radius = 80;

    const principalAngle = (principalPercent / 100) * 360;
    const interestAngle = 360 - principalAngle;

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    };

    const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return [
        "M", x, y,
        "L", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "Z"
      ].join(" ");
    };

    return (
      <svg width="200" height="200" style={{ display: 'inline-block', margin: '1rem 0' }}>
        <path d={describeArc(cx, cy, radius, 0, principalAngle)} fill={colors.primary} />
        <path d={describeArc(cx, cy, radius, principalAngle, 360)} fill={colors.accent} />
        <text
          x={cx + radius * 0.5 * Math.cos((principalAngle / 2 - 90) * Math.PI / 180)}
          y={cy + radius * 0.5 * Math.sin((principalAngle / 2 - 90) * Math.PI / 180)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontWeight="bold"
          fontSize="16"
        >
          {Math.round(principalPercent)}%
        </text>
        <text
          x={cx + radius * 0.5 * Math.cos(((principalAngle + (interestAngle / 2)) - 90) * Math.PI / 180)}
          y={cy + radius * 0.5 * Math.sin(((principalAngle + (interestAngle / 2)) - 90) * Math.PI / 180)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontWeight="bold"
          fontSize="16"
        >
          {Math.round(interestPercent)}%
        </text>
      </svg>
    );
  };

  const handleSimpleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { balance, term, rate } = simpleInputs;
    const months = term * 12;
    const monthlyRate = (rate / 100) / 12;

    const monthlyPayment = balance * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = monthlyPayment * months;
    const totalInterest = totalPayment - balance;

    setSimpleResults({
      monthlyPayment,
      totalInterest,
      totalPayment,
      principal: balance
    });
  };

  const handleRepaymentCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { balance, payment, rate, extraMonthly, extraYearly, extraOnetime } = repaymentInputs;
    const monthlyRate = (rate / 100) / 12;

    let origMonths = 0;
    let origBalance = balance;
    let origInterest = 0;

    while (origBalance > 0 && origMonths < 600) {
      const interest = origBalance * monthlyRate;
      origInterest += interest;
      origBalance = origBalance + interest - payment;
      origMonths++;
    }

    const origTotal = balance + origInterest;

    let newMonths = 0;
    let newBalance = balance - (showExtraPayments ? extraOnetime : 0);
    let newInterest = 0;

    if (showExtraPayments) {
      while (newBalance > 0 && newMonths < 600) {
        const interest = newBalance * monthlyRate;
        newInterest += interest;
        let totalPayment = payment + extraMonthly;

        if (newMonths > 0 && newMonths % 12 === 0) {
          totalPayment += extraYearly;
        }

        newBalance = newBalance + interest - totalPayment;
        newMonths++;
      }
    } else {
      newMonths = origMonths;
      newInterest = origInterest;
    }

    const newTotal = balance + newInterest;
    const savings = origInterest - newInterest;
    const timeSaved = origMonths - newMonths;

    setRepaymentResults({
      origMonths,
      origInterest,
      origTotal,
      newMonths,
      newInterest,
      newTotal,
      savings,
      timeSaved
    });
  };

  const handleProjectionCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { yearsToGrad, annualAmount, currentBalance, term, graceMonths, rate, payInterest } = projectionInputs;
    const monthlyRate = (rate / 100) / 12;
    const totalBorrowed = currentBalance + (annualAmount * yearsToGrad);

    let balanceAtGrad = currentBalance;

    if (!payInterest) {
      const monthsInSchool = yearsToGrad * 12;
      for (let i = 0; i < monthsInSchool; i++) {
        balanceAtGrad += balanceAtGrad * monthlyRate;
      }

      for (let year = 1; year <= yearsToGrad; year++) {
        let yearBalance = annualAmount;
        const monthsRemaining = (yearsToGrad - year) * 12;
        for (let m = 0; m < monthsRemaining; m++) {
          yearBalance += yearBalance * monthlyRate;
        }
        balanceAtGrad += yearBalance;
      }
    } else {
      balanceAtGrad = totalBorrowed;
    }

    let balanceAfterGrace = balanceAtGrad;
    for (let i = 0; i < graceMonths; i++) {
      balanceAfterGrace += balanceAfterGrace * monthlyRate;
    }

    const repaymentMonths = term * 12;
    const monthlyPayment = balanceAfterGrace * (monthlyRate * Math.pow(1 + monthlyRate, repaymentMonths)) /
                          (Math.pow(1 + monthlyRate, repaymentMonths) - 1);
    const totalPayment = monthlyPayment * repaymentMonths;
    const totalInterest = totalPayment - totalBorrowed;

    setProjectionResults({
      monthlyPayment,
      totalBorrowed,
      balanceAtGrad,
      balanceAfterGrace,
      totalInterest,
      principal: totalBorrowed
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>🎓 {t("toolTitle")}</h2>
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={styles.tabButton(activeTab === 'simple')}
              onClick={() => setActiveTab('simple')}
            >
              {t("simpleTab")}
            </button>
            <button
              style={styles.tabButton(activeTab === 'repayment')}
              onClick={() => setActiveTab('repayment')}
            >
              {t("repaymentTab")}
            </button>
            <button
              style={styles.tabButton(activeTab === 'projection')}
              onClick={() => setActiveTab('projection')}
            >
              {t("projectionTab")}
            </button>
          </div>

          {activeTab === 'simple' && (
            <div style={styles.calculatorGrid}>
              <div style={styles.calculatorForm}>
                <form onSubmit={handleSimpleCalculate}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="simple-balance">{t("loanBalance")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="simple-balance"
                        value={simpleInputs.balance}
                        onChange={(e) => setSimpleInputs({...simpleInputs, balance: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithPrefix}}
                        step="100"
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="simple-term">{t("loanTerm")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="simple-term"
                        value={simpleInputs.term}
                        onChange={(e) => setSimpleInputs({...simpleInputs, term: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.5"
                        required
                      />
                      <span style={styles.inputSuffix}>{t("yearsUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="simple-rate">{t("interestRate")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="simple-rate"
                        value={simpleInputs.rate}
                        onChange={(e) => setSimpleInputs({...simpleInputs, rate: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.1"
                        required
                      />
                      <span style={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <button type="submit" style={styles.calculateButton}>
                    {t("calculatePayment")}
                  </button>
                </form>
              </div>

              {simpleResults && (
                <div style={styles.results}>
                  <h3 style={styles.h2}>📊 {t("results")}</h3>
                  <div style={styles.resultItem(true)}>
                    <span style={styles.resultLabel}>{t("monthlyPayment")}</span>
                    <span style={styles.resultValue(true)}>{formatCurrency(simpleResults.monthlyPayment)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("totalInterest")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(simpleResults.totalInterest)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("totalPayments")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(simpleResults.totalPayment)}</span>
                  </div>

                  <div style={styles.chartContainer}>
                    {drawPieChart(simpleResults.principal, simpleResults.totalInterest)}
                    <div style={styles.chartLegend}>
                      <div style={styles.legendItem}>
                        <div style={styles.legendColor(colors.primary)}></div>
                        <span>{t("principal")}</span>
                      </div>
                      <div style={styles.legendItem}>
                        <div style={styles.legendColor(colors.accent)}></div>
                        <span>{t("interest")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'repayment' && (
            <div style={styles.calculatorGrid}>
              <div style={styles.calculatorForm}>
                <form onSubmit={handleRepaymentCalculate}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="repay-balance">{t("loanBalance")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="repay-balance"
                        value={repaymentInputs.balance}
                        onChange={(e) => setRepaymentInputs({...repaymentInputs, balance: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithPrefix}}
                        step="100"
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="repay-payment">{t("currentMonthlyPayment")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="repay-payment"
                        value={repaymentInputs.payment}
                        onChange={(e) => setRepaymentInputs({...repaymentInputs, payment: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithPrefix}}
                        step="10"
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="repay-rate">{t("interestRate")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="repay-rate"
                        value={repaymentInputs.rate}
                        onChange={(e) => setRepaymentInputs({...repaymentInputs, rate: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.1"
                        required
                      />
                      <span style={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("repaymentOptions")}</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="repay-option"
                          checked={!showExtraPayments}
                          onChange={() => setShowExtraPayments(false)}
                        />
                        <span>{t("normalRepayment")}</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="repay-option"
                          checked={showExtraPayments}
                          onChange={() => setShowExtraPayments(true)}
                        />
                        <span>{t("extraPayments")}</span>
                      </label>
                      <div style={styles.extraPayments(showExtraPayments)}>
                        <div style={styles.formGroup}>
                          <label style={styles.label} htmlFor="extra-monthly">{t("extraMonthlyPayment")}</label>
                          <div style={styles.inputWrapper}>
                            <span style={styles.inputPrefix}>$</span>
                            <input
                              type="number"
                              id="extra-monthly"
                              value={repaymentInputs.extraMonthly}
                              onChange={(e) => setRepaymentInputs({...repaymentInputs, extraMonthly: parseFloat(e.target.value)})}
                              style={{...styles.input, ...styles.inputWithPrefix}}
                              step="10"
                            />
                          </div>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label} htmlFor="extra-yearly">{t("extraYearlyPayment")}</label>
                          <div style={styles.inputWrapper}>
                            <span style={styles.inputPrefix}>$</span>
                            <input
                              type="number"
                              id="extra-yearly"
                              value={repaymentInputs.extraYearly}
                              onChange={(e) => setRepaymentInputs({...repaymentInputs, extraYearly: parseFloat(e.target.value)})}
                              style={{...styles.input, ...styles.inputWithPrefix}}
                              step="100"
                            />
                          </div>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label} htmlFor="extra-onetime">{t("oneTimePayment")}</label>
                          <div style={styles.inputWrapper}>
                            <span style={styles.inputPrefix}>$</span>
                            <input
                              type="number"
                              id="extra-onetime"
                              value={repaymentInputs.extraOnetime}
                              onChange={(e) => setRepaymentInputs({...repaymentInputs, extraOnetime: parseFloat(e.target.value)})}
                              style={{...styles.input, ...styles.inputWithPrefix}}
                              step="100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button type="submit" style={styles.calculateButton}>
                    {t("calculateSavings")}
                  </button>
                </form>
              </div>

              {repaymentResults && (
                <div style={styles.results}>
                  <h3 style={styles.h2}>💰 {t("comparison")}</h3>

                  <div style={styles.comparisonTable}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}></th>
                          <th style={styles.th}>{t("withExtraPayments")}</th>
                          <th style={styles.th}>{t("originalPlan")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={styles.td}><strong>{t("payoffTime")}</strong></td>
                          <td style={styles.td}>{formatTime(repaymentResults.newMonths)}</td>
                          <td style={styles.td}>{formatTime(repaymentResults.origMonths)}</td>
                        </tr>
                        <tr>
                          <td style={styles.td}><strong>{t("totalInterest")}</strong></td>
                          <td style={styles.td}>{formatCurrency(repaymentResults.newInterest)}</td>
                          <td style={styles.td}>{formatCurrency(repaymentResults.origInterest)}</td>
                        </tr>
                        <tr>
                          <td style={styles.td}><strong>{t("totalPayments")}</strong></td>
                          <td style={styles.td}>{formatCurrency(repaymentResults.newTotal)}</td>
                          <td style={styles.td}>{formatCurrency(repaymentResults.origTotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {showExtraPayments && repaymentResults.savings > 0 && (
                    <div style={styles.savingsHighlight}>
                      <h4 style={styles.savingsTitle}>💡 {t("yourSavings")}</h4>
                      <p style={styles.savingsText}>
                        {t("savingsMessage", {
                          savings: formatCurrency(repaymentResults.savings),
                          time: formatTime(repaymentResults.timeSaved)
                        })}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'projection' && (
            <div style={styles.calculatorGrid}>
              <div style={styles.calculatorForm}>
                <form onSubmit={handleProjectionCalculate}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-years">{t("yearsUntilGraduation")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="proj-years"
                        value={projectionInputs.yearsToGrad}
                        onChange={(e) => setProjectionInputs({...projectionInputs, yearsToGrad: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.5"
                        required
                      />
                      <span style={styles.inputSuffix}>{t("yearsUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-annual">{t("estimatedAnnualLoan")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="proj-annual"
                        value={projectionInputs.annualAmount}
                        onChange={(e) => setProjectionInputs({...projectionInputs, annualAmount: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithPrefix}}
                        step="500"
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-current">{t("currentBalance")}</label>
                    <div style={styles.inputWrapper}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        id="proj-current"
                        value={projectionInputs.currentBalance}
                        onChange={(e) => setProjectionInputs({...projectionInputs, currentBalance: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithPrefix}}
                        step="500"
                        required
                      />
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-term">{t("repaymentTerm")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="proj-term"
                        value={projectionInputs.term}
                        onChange={(e) => setProjectionInputs({...projectionInputs, term: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.5"
                        required
                      />
                      <span style={styles.inputSuffix}>{t("yearsUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-grace">{t("gracePeriod")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="proj-grace"
                        value={projectionInputs.graceMonths}
                        onChange={(e) => setProjectionInputs({...projectionInputs, graceMonths: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="1"
                        required
                      />
                      <span style={styles.inputSuffix}>{t("monthsUnit")}</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="proj-rate">{t("interestRate")}</label>
                    <div style={styles.inputWrapper}>
                      <input
                        type="number"
                        id="proj-rate"
                        value={projectionInputs.rate}
                        onChange={(e) => setProjectionInputs({...projectionInputs, rate: parseFloat(e.target.value)})}
                        style={{...styles.input, ...styles.inputWithSuffix}}
                        step="0.1"
                        required
                      />
                      <span style={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("payInterestDuringSchool")}</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="proj-interest"
                          checked={projectionInputs.payInterest}
                          onChange={() => setProjectionInputs({...projectionInputs, payInterest: true})}
                        />
                        <span>{t("yes")}</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="proj-interest"
                          checked={!projectionInputs.payInterest}
                          onChange={() => setProjectionInputs({...projectionInputs, payInterest: false})}
                        />
                        <span>{t("no")}</span>
                      </label>
                    </div>
                  </div>

                  <button type="submit" style={styles.calculateButton}>
                    {t("projectFutureDebt")}
                  </button>
                </form>
              </div>

              {projectionResults && (
                <div style={styles.results}>
                  <h3 style={styles.h2}>🔮 {t("futureProjection")}</h3>
                  <div style={styles.resultItem(true)}>
                    <span style={styles.resultLabel}>{t("monthlyPayment")}</span>
                    <span style={styles.resultValue(true)}>{formatCurrency(projectionResults.monthlyPayment)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("amountBorrowed")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(projectionResults.totalBorrowed)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("balanceAtGraduation")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(projectionResults.balanceAtGrad)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("balanceAfterGrace")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(projectionResults.balanceAfterGrace)}</span>
                  </div>
                  <div style={styles.resultItem()}>
                    <span style={styles.resultLabel}>{t("totalInterest")}</span>
                    <span style={styles.resultValue()}>{formatCurrency(projectionResults.totalInterest)}</span>
                  </div>

                  <div style={styles.chartContainer}>
                    {drawPieChart(projectionResults.principal, projectionResults.totalInterest)}
                    <div style={styles.chartLegend}>
                      <div style={styles.legendItem}>
                        <div style={styles.legendColor(colors.primary)}></div>
                        <span>{t("principal")}</span>
                      </div>
                      <div style={styles.legendItem}>
                        <div style={styles.legendColor(colors.accent)}></div>
                        <span>{t("interest")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introPara1")}</p>
        <p style={styles.paragraph}>{t("introPara2")}</p>
        <p style={styles.paragraph}>{t("introPara3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("step1Title")}</span> {t("step1Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step2Title")}</span> {t("step2Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step3Title")}</span> {t("step3Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step4Title")}</span> {t("step4Desc")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>

        <div style={styles.insightCard}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>
            {t("insight1Title")}
          </h3>
          <p style={styles.paragraph}>{t("insight1Desc")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>
            {t("insight2Title")}
          </h3>
          <p style={styles.paragraph}>{t("insight2Desc")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>
            {t("insight3Title")}
          </h3>
          <p style={styles.paragraph}>{t("insight3Desc")}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>
            {t("insight4Title")}
          </h3>
          <p style={styles.paragraph}>{t("insight4Desc")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
          {t("detailSubtitle1")}
        </h3>
        <p style={styles.paragraph}>{t("detailPara1")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
          {t("detailSubtitle2")}
        </h3>
        <p style={styles.paragraph}>{t("detailPara2")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
          {t("detailSubtitle3")}
        </h3>
        <p style={styles.paragraph}>{t("detailPara3")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
          {t("detailSubtitle4")}
        </h3>
        <p style={styles.paragraph}>{t("detailPara4")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.studentloanplanner.com/free-student-loan-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.tateesq.com/calculator/save-plan"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.credible.com/student-loans/student-loan-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref3")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.salliemae.com/college-planning/tools/student-loan-repayment-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref4")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://studentaid.gov/articles/compare-student-loan-repayment-plans-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref5")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.nerdwallet.com/student-loans/learn/student-loan-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("ref6")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}
